// exemple
// element('.about'); // <div class="about">
// element('[data-action="top-nav"]'); // <div class="nav" data-action="top-nav">
//export const element  = (query: string) => document.querySelector(query);
export const element = (query: string) => document.querySelector<any>(query);

export const elements = (query: string) => document.querySelectorAll<any>(query);

export const elementsOf = (element: Element | HTMLElement, query: string) => element.querySelector<any>(query);

// exemple
// elementContains(document.querySelector('head'), document.querySelector('title')); // true
// elementContains(document.querySelector('body'), document.querySelector('body')); // false
export const elementContains = (parent: Element, child: Element) => parent !== child && parent.contains(child);

export const on = (action: string | Element, type: string, callback: Function, ref?: object, meta?: object) => {
  docReady(() => {
    type.split(' ').forEach(function (t) {
      (action instanceof Element ? action : element(action)).addEventListener(
        t,
        (e: Event) => {
          try {
            callback.call(e.target, e, ref ?? null, meta ?? null);
          } catch (err) {}
        },
        { once: false, passive: true }
      );
    });
  });
};

export const one = (action: string | Element, type: string, callback: Function) => {
  docReady(() => {
    type.split(' ').forEach(function (e) {
      (action instanceof Element ? action : element(action)).addEventListener(e, callback, { once: true });
    });
  });
};

export const event = (action: string, type: string, callback: EventListener | EventListenerObject) => {
  docReady(() => {
    element(`[data-event="${action}"]`)?.addEventListener(type, callback, false);
  });
};

export const events = (action: string, type: string, callback: EventListener | EventListenerObject) => {
  docReady(() => {
    elements(`[data-event="${action}"]`)?.forEach((item) => {
      item.addEventListener(type, callback, false);
    });
  });
};

export const action = (action: string, type: string, callback: EventListener | EventListenerObject) => {
  docReady(() => {
    element(`[data-action="${action}"]`)?.addEventListener(type, callback, false);
  });
};

export const actions = (action: string, type: string, callback: EventListener | EventListenerObject | Function) => {
  docReady(() => {
    elements(`[data-action="${action}"]`)?.forEach((el) => el?.addEventListener(type, callback, false));
  });
};

export const bind = (action: string, type: string, callback: EventListener | EventListenerObject) => {
  docReady(() => {
    elements(action || '')?.forEach((item) => {
      type.split(' ').forEach((t) => {
        item.addEventListener(t, callback, false);
      });
    });
  });
};

export const off = (
  action: string | Element | HTMLElement | Array<string | Element | HTMLElement>,
  type: string,
  callback: EventListener | EventListenerObject
) => {
  if (action instanceof String || action instanceof Element || action instanceof HTMLElement) {
    (action instanceof Element || action instanceof HTMLElement ? action : element(<string>action)).removeEventListener(type, callback, false);
  }

  if (action instanceof Array) {
    for (let x of action) {
      let item: any = x;
      (item instanceof Element || item instanceof HTMLElement ? item : element(<string>item)).removeEventListener(type, callback, false);
    }
  }
};

export const offAll = (action: string, type: string, callback: EventListener | EventListenerObject) => {
  elements(action).forEach((item) => {
    item.removeEventListener(type, callback, false);
  });
};

export interface IEventChain {
  target: string;
  type: string;
  events: Array<IEventChainItem>;
}

export interface IEventChainItem {
  action?: string;
  event?: string;
  callback?: Function;
}

export const eventChain = (arg: IEventChain) => {
  bind(arg.target, arg.type, (e: Event) => {
    const target: any = e.target;
    let eventTarget = target?.getAttribute('data-action') ?? target?.getAttribute('data-event');

    if (!eventTarget) eventTarget = target.parentNode?.getAttribute('data-action') ?? target.parentNode?.getAttribute('data-event');

    if (!eventTarget) return;

    const item: any = arg.events.find(({ action, event }) => (action ?? event ?? '').includes(eventTarget as string));
    if (item?.callback) item.callback(e);
  });
};

export const getBlazorEditMode = () => {
  if (!window.isAdminModeEnabled) {
    return false;
  }
  if (window.isEditModeEnabled !== undefined) {
    // Return value to avoid parsing cookies constantly
    return window.isEditModeEnabled;
  }
  const cookies = document.cookie.split(';');
  const editModeCookie = cookies.find((cookie) => cookie.trim().startsWith('_AdminEditMode'));
  window.isEditModeEnabled = editModeCookie?.split('=')[1].toLowerCase() === 'true';
  return window.isEditModeEnabled;
};

// Need to wait for blazor

export const docReady = (callback?: Function, context?: Object | string | number | boolean) => {
  let blazorFinishedLoadingEventRegistered: boolean = true;

  // Method to trigger window events
  if (!window.EasyfyMethods) {
    window.EasyfyMethods = {};
    window.EasyfyMethods.triggerWindowEvent = (eventName: string, eventData?: unknown) => {
      if (eventName) {
        let event = new CustomEvent(eventName, {
          detail: eventData,
        });
        window.dispatchEvent(event);
      }
    };
  }

  if (getBlazorEditMode()) {
    blazorFinishedLoadingEventRegistered = false;
  }

  // The public function name defaults to window.docReady
  // but you can pass in your own object and own function name and those will be used
  // if you want to put them in a different namespace
  // funcName = funcName || "docReady";
  // baseObj = baseObj || window;
  let readyList: Array<{ fn: Function; ctx: Object | string | number | boolean | undefined }> = [];
  let readyFired = false;
  let readyEventHandlersInstalled = false;

  // call this when the document is ready
  // this function protects itself against being called more than once
  const ready = () => {
    const readyBody = () => {
      if (!readyFired) {
        // this must be set to true before we start calling callbacks
        readyFired = true;
        for (var i = 0; i < readyList.length; i++) {
          // if a callback here happens to add new ready handlers,
          // the docReady() function will see that it already fired
          // and will schedule the callback to run right after
          // this event loop finishes so all handlers will still execute
          // in order and no new ones will be added to the readyList
          // while we are processing the list
          readyList[i].fn.call(window, readyList[i].ctx);
          // allow any closures held by these functions to free
        }
        readyList = [];
      }
    };
    if (getBlazorEditMode() && !window.blazorFinishedLoading) {
      if (!blazorFinishedLoadingEventRegistered) {
        blazorFinishedLoadingEventRegistered = true;
        window.addEventListener(
          'blazorFinishedLoading',
          () => {
            window.blazorFinishedLoading = true;
            readyBody();
          },
          false
        );
      }
    } else {
      readyBody();
    }
  };

  const readyStateChange = () => {
    if (document.readyState === 'complete') {
      ready();
    }
  };

  // This is the one public interface
  // docReady(fn, context);
  // the context argument is optional - if present, it will be passed
  // as an argument to the callback

  if (typeof callback !== 'function') {
    throw new TypeError('callback for docReady(fn) must be a function');
  }
  // if ready has already fired, then just schedule the callback
  // to fire asynchronously, but right away
  if (readyFired) {
    setTimeout(function () {
      callback(context);
    }, 1);
    return;
  } else {
    // add the function and context to the list
    readyList.push({ fn: callback, ctx: context });
  }
  // if document already ready to go, schedule the ready function to run
  if (document.readyState === 'complete') {
    setTimeout(ready, 1);
  } else if (!readyEventHandlersInstalled) {
    // otherwise if we don't have event handlers installed, install them
    if (document.addEventListener) {
      // first choice is DOMContentLoaded event
      document.addEventListener('DOMContentLoaded', ready, false);
      // backup is window load event
      window.addEventListener('load', ready, false);
    }
    readyEventHandlersInstalled = true;
  }
};

export interface IEvent {
  type: string;
  event?: IEventItem;
  events?: Array<IEventItem>;
}

export interface IEventItem {
  action?: string;
  event?: string;
  callback?: Function;
}

interface IGlobalEventItem {
  type: string;
  action?: string;
  event?: string;
  callback?: Array<Function>;
}

// Global event
export let globalEventList: Array<IGlobalEventItem> = [];

export const globalEvent = (event: IEvent) => {
  event.type.split(' ').forEach((type) => {
    if (event.event) event.events = [...(event.events ?? []), ...[event.event]];

    if (event.events) {
      for (let item of event.events) {
        let foundItem: IGlobalEventItem | undefined;

        if (item?.action) {
          const actionFound = globalFindAction(type, item?.action);
          if (actionFound) {
            foundItem = actionFound;
          }
        }

        if (item?.event) {
          const eventFound = globalFindEvent(type, item?.event);
          if (eventFound) {
            foundItem = eventFound;
          }
        }

        if (!item?.event && !item?.action) {
          // check global state.
          const globalFound = globalFind(type);
          if (globalFound) {
            foundItem = globalFound;
          }
        }

        if (foundItem) {
          if (item?.callback) {
            (foundItem.callback ??= []).push(item.callback);
          }
        } else {
          globalEventAdd(type, item);
        }
      }
    }

    document.body.removeEventListener(type, globalEventHandler);
    document.body.addEventListener(type, globalEventHandler);
  });
};

export const debounce = (func: (...args: any[]) => unknown, wait: number) => {
  let timeout: ReturnType<typeof setTimeout> | undefined;

  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const globalEventHandler = (event: Event) => {
  // Process actions
  const actionProcessed = processEventTarget(event, globalGetTargetAction, globalFindAction);

  // Process events
  const eventProcessed = processEventTarget(event, globalGetTargetEvent, globalFindEvent);

  // Process global type match only if no actions or events were processed
  if (!actionProcessed && !eventProcessed) {
    const item = globalMatchType(event.type);
    if (item?.callback) {
      globalEventInvokeCallback(item.callback, event);
    }
  }
};

const globalEventInvokeCallback = (cbs: Array<Function>, e: any) => {
  for (let item of cbs) {
    item?.call(null, e);
  }
};

const globalEventAdd = (type: string, item: IEventItem): void => {
  globalEventList.push({
    type: type,
    action: item?.action,
    event: item?.event,
    callback: item?.callback ? [item.callback] : [],
  });
};

const processEventTarget = (
  event: Event,
  getTargetFn: (t: any) => string | undefined,
  findEventFn: (type: string, name: string) => IGlobalEventItem | undefined
): boolean => {
  const eventTarget = getTargetFn(event.target);
  if (!eventTarget) {
    return false;
  }
  // Split comma separated event names
  const eventNames = eventTarget
    .split(',')
    .map((name: string) => name.trim())
    .filter((name: string) => name !== '');

  for (const eventName of eventNames) {
    const item = findEventFn(event.type, eventName);
    if (item?.callback) {
      globalEventInvokeCallback(item.callback, event);
    }
  }

  return true;
};

const globalFind = (itemType: string): IGlobalEventItem | undefined => {
  return globalEventList.find(({ type, action, event }) => type === itemType && !action && !event);
};

const globalFindEvent = (itemType: string, eventType: string): IGlobalEventItem | undefined =>
  globalEventList.find(({ type, event }) => type === itemType && (event ?? '') === (eventType ?? ''));

const globalFindAction = (itemType: string, actionType: string): IGlobalEventItem | undefined =>
  globalEventList.find(({ type, action }) => type === itemType && (action ?? '') === (actionType ?? ''));

const globalMatchType = (itemType: string): IGlobalEventItem | undefined =>
  globalEventList.find(({ type, action, event }) => type === itemType && !action && !event);

const globalGetTargetAction = (target: any) => globalGetTarget(target, 'data-action');

const globalGetTargetEvent = (target: any) => globalGetTarget(target, 'data-event');

const globalGetTarget = (target: any, dataTarget: string) => {
  let eventTarget = target?.attributes[dataTarget]?.value;
  if (!eventTarget) eventTarget = target.parentNode?.attributes[dataTarget]?.value;
  return eventTarget;
};
